// A mobile two-line list row with swipe-to-reveal actions (Edit / Delete), used by the
// register lists on a phone in place of a cramped multi-column table. At rest the whole
// row width is data; a swipe left slides the content aside to reveal the actions behind
// it, and a plain tap navigates (the row's onTap). Desktop lists never render this (the
// consumer gates it to below the md breakpoint), so it is purely additive.
//
// Self-contained: the useSwipeReveal hook handles touchstart/move/end, distinguishes a
// horizontal swipe from a vertical scroll (so the page still scrolls) and from a tap (so
// the row still navigates), and snaps open/closed on release. No external dependency.
import { useRef, useState } from "react";
import type { ReactNode, TouchEvent, KeyboardEvent } from "react";

// px width of one action button; two actions (Edit + Delete) reveal 2 x this.
const ACTION_W = 88;
// Horizontal movement (px) past which a gesture counts as a swipe, not a tap.
const SWIPE_THRESHOLD = 10;

export function useSwipeReveal(actionsWidth: number, threshold = SWIPE_THRESHOLD) {
  const [open, setOpen] = useState(false);
  // The live drag offset while a finger is down (null when not dragging).
  const [dragX, setDragX] = useState<number | null>(null);
  const startRef = useRef<{ x: number; y: number; open: boolean } | null>(null);
  // True once the current gesture has moved horizontally past the threshold (a swipe, not
  // a tap). A ref so the row's click handler can read it synchronously.
  const moved = useRef(false);

  function onTouchStart(e: TouchEvent) {
    const t = e.touches[0];
    if (!t) return;
    startRef.current = { x: t.clientX, y: t.clientY, open };
    moved.current = false;
  }
  function onTouchMove(e: TouchEvent) {
    const s = startRef.current;
    const t = e.touches[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    // A mostly-vertical gesture is a page scroll: leave it alone until a horizontal drag begins.
    if (!moved.current && Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) > threshold) moved.current = true;
    if (!moved.current) return;
    const base = s.open ? -actionsWidth : 0;
    setDragX(Math.max(-actionsWidth, Math.min(0, base + dx)));
  }
  function onTouchEnd() {
    const wasMoved = moved.current;
    const finalDrag = dragX;
    setDragX(null);
    startRef.current = null;
    // A tap (no horizontal movement) leaves the open state untouched: the row's onClick acts.
    if (!wasMoved) return;
    // A swipe snaps open past the halfway point, else closed.
    if (finalDrag !== null) setOpen(finalDrag <= -actionsWidth / 2);
  }

  const dragging = dragX !== null;
  const offset = dragging ? (dragX as number) : open ? -actionsWidth : 0;
  return {
    open,
    offset,
    dragging,
    moved,
    close: () => setOpen(false),
    swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}

export interface SwipeListRowProps {
  // Line 1: the primary identifier (Rajdhani, white, wraps, never truncated).
  title: ReactNode;
  // Line 2 left: a muted secondary line (e.g. the customer name).
  subtitle?: ReactNode;
  // Line 2 right: a reference or status pill.
  meta?: ReactNode;
  // Tap (no swipe) action, e.g. navigate to the detail page.
  onTap?: () => void;
  // When provided, an Edit action is revealed on swipe left.
  onEdit?: () => void;
  // When provided, a Delete action is revealed on swipe left (behind Edit).
  onDelete?: () => void;
  // Accessible name for the tappable row.
  ariaLabel?: string;
}

export function SwipeListRow({ title, subtitle, meta, onTap, onEdit, onDelete, ariaLabel }: SwipeListRowProps) {
  const actionCount = (onEdit ? 1 : 0) + (onDelete ? 1 : 0);
  const revealW = actionCount * ACTION_W;
  const { open, offset, dragging, moved, close, swipeHandlers } = useSwipeReveal(revealW);

  function handleTap() {
    // The click that fires at the end of a swipe is not a tap: swallow it.
    if (moved.current) {
      moved.current = false;
      return;
    }
    // A tap while the row is open closes it rather than navigating.
    if (open) {
      close();
      return;
    }
    onTap?.();
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTap?.();
    }
  }

  return (
    <li className="relative overflow-hidden border-t border-ele-purple-accent first:border-t-0" data-open={open}>
      {actionCount > 0 && (
        <div className="absolute inset-y-0 right-0 flex" aria-hidden={!open}>
          {onEdit && (
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={() => {
                onEdit();
                close();
              }}
              style={{ width: ACTION_W }}
              className="flex min-h-[44px] items-center justify-center bg-ele-green font-menu text-sm tracking-wide text-ele-bg"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={() => {
                onDelete();
                close();
              }}
              style={{ width: ACTION_W }}
              className="flex min-h-[44px] items-center justify-center bg-ele-danger-fill font-menu text-sm tracking-wide text-white"
            >
              Delete
            </button>
          )}
        </div>
      )}
      {/* The row surface must stay OPAQUE: it slides over the action buttons above, so a
          translucent wash would show them through. It takes the shell token (Session AFK) so the
          light theme gives the same white card the desktop table wrapper gets; dark is the
          unchanged #050816. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleTap}
        onKeyDown={handleKeyDown}
        {...swipeHandlers}
        style={{ transform: `translateX(${offset}px)`, transition: dragging ? "none" : "transform 0.2s ease" }}
        className="relative flex min-h-[44px] items-start gap-2 bg-[var(--ele-shell)] px-3 py-3"
      >
        <div className="min-w-0 flex-1">
          <div className="font-heading text-base text-ele-strong break-words">{title}</div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-xs text-ele-faint">{subtitle}</span>
            {meta && <span className="shrink-0 text-xs">{meta}</span>}
          </div>
        </div>
        {/* Chevron: signals the row is tappable (opens the detail page). */}
        <svg aria-hidden viewBox="0 0 16 16" className="mt-0.5 h-4 w-4 shrink-0 fill-current text-ele-purple">
          <path d="M6 4l4 4-4 4z" />
        </svg>
      </div>
    </li>
  );
}
